import { formatCurrency } from '@/components/molecules/financialImpact';
import type { PortalPillTone } from '@/components/portal/PortalStatusPill';

/** The unit a series' price is quoted in (see `recurringPlanPrice` server-side). */
export type RecurringPricePer = 'VISIT' | 'MONTH' | 'QUARTER' | 'YEAR' | 'TERM';

/** What a series charges: a subscription's fee, a prepaid term's, or a visit's price. */
export interface RecurringPlanPrice {
  amount: number;
  per: RecurringPricePer;
}

const PER_LABEL: Record<RecurringPricePer, string> = {
  VISIT: ' per visit',
  MONTH: ' / month',
  QUARTER: ' / quarter',
  YEAR: ' / year',
  TERM: ' for the term',
};

/** "$99.00 / month", "$50.00 per visit", "$1,000.00 for the term". */
export function formatPlanPrice(price: RecurringPlanPrice): string {
  return `${formatCurrency(price.amount)}${PER_LABEL[price.per]}`;
}

const STATUS: Record<string, { label: string; tone: PortalPillTone }> = {
  ACTIVE: { label: 'Active', tone: 'success' },
  PAUSED: { label: 'Paused', tone: 'neutral' },
  PAST_DUE: { label: 'Past due', tone: 'accent' },
  SUSPENDED: { label: 'Suspended', tone: 'accent' },
  ENDED: { label: 'Ended', tone: 'neutral' },
};

/** A recurring service's status as the customer reads it ("Past due", not "PAST_DUE"). */
export function recurringPlanStatus(status: string): { label: string; tone: PortalPillTone } {
  return (
    STATUS[status] ?? { label: status.charAt(0) + status.slice(1).toLowerCase(), tone: 'neutral' }
  );
}
